import React, {useEffect, useState, useMemo, useRef} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import meQuery from '../graphql/queries/meQuery';
import isQueryReady from '../utils/isQueryReady';
import useCallbackRef from '../hooks/useCallbackRef';
import useVideoSource from '../hooks/useVideoSource';
import getAuthenticationToken from '../utils/getAuthenticationToken';
import updatePreferencesMutation from '../graphql/mutations/updatePreferencesMutation';
import styled, { css, useTheme } from "styled-components";
import Heading from './content/Heading';
import Text from './content/Text';
import Stack from './layout/Stack';
import Box from './layout/Box';
import { useTranslation } from 'react-i18next';
import togglePlayVideo from "../utils/togglePlayVideo";
import { animated, useTransition } from "react-spring";
import IconPause from './iconography/IconPause';
import IconPlay from './iconography/IconPlay';

const DEFAULT_VIDEO_STYLE = {
  width: '100%',
  height: '100%',
};

const hlsConfig = {
  capLevelToPlayerSize: true,
  startLevel: -1,
};

const DEFAULT_LISTENERS = {};

const DEFAULT_MEDIA_ERROR = {
  code: -1,
  message: undefined,
};

const VIDEO_PLAY_STATE_PLAYING = 0;
const VIDEO_PLAY_STATE_PAUSED = 1;
const VIDEO_PLAY_STATE_ENDED = 2;

function playStateTransitionValuesFactory(videoPlayState, deviceInfo) {
  const common = {
    config: { duration: 600 },
    from: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) scale(0.5)',
      opacity: 1,
      pointerEvents: 'none',
    },
  };

  if (deviceInfo.isMobile) {
    return {
      ...common,
      enter: {
        transform: videoPlayState === VIDEO_PLAY_STATE_PAUSED ? 'translate(-50% -50%) scale(0.5)' : 'translate(-50% -50%) scale(1.0)',
        opacity: videoPlayState === VIDEO_PLAY_STATE_PAUSED ? 1 : 0,
      },
      leave: {
        transform: videoPlayState === VIDEO_PLAY_STATE_PLAYING ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(1.2)',
        opacity: 0,
      },
    };
  } else {
    return {
      ...common,
      enter: {
        transform: 'translate(-50% -50%) scale(1.0)',
        opacity: 0,
      },
      leave: {
        transform: 'translate(-50%, -50%) scale(1.2)',
        opacity: 0,
      },
    }
  }
}

export default React.memo(React.forwardRef(function Video({
  source,
  listeners: userListeners = DEFAULT_LISTENERS,
  keyboardShortcuts = false,
  playPauseFeedback = false,
  style,
  muted = undefined,
  ...props
}, forwardingReference) {
  const { t } = useTranslation();
  const me = useQuery(meQuery, {
    skip: getAuthenticationToken() === undefined,
  });
  const [reference, element] = useCallbackRef();
  const [attachMedia, , loadedSource] = useVideoSource(source, hlsConfig);
  const [mediaError, setMediaError] = useState(DEFAULT_MEDIA_ERROR);

  const { deviceInfo } = useTheme();

  const [videoPlayState, setVideoPlayState] = useState(VIDEO_PLAY_STATE_PAUSED);
  // This variable is used to assess if the user has interacred with the video or if the states (play/pause) come from an automated process.
  // This is used to show or hide icon feedback to the user.
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const userInteractedTimeoutId = useRef(null);

  useEffect(function() {
    // no-op
    return function() {
      clearTimeout(userInteractedTimeoutId.current);
    }
  }, []);

  const videoStyle = useMemo(() => ({
    ...DEFAULT_VIDEO_STYLE,
    ...style,
  }), [style]);

  const defaultListeners = useMemo(() => ({
    play: function() {
      if (playPauseFeedback === true) {
        setVideoPlayState(VIDEO_PLAY_STATE_PLAYING);
        userInteractedTimeoutId.current = setTimeout(() => setHasUserInteracted(false), 600);
      }
    },
    pause: function(event) {
      // When the video ends, both the pause and ended events are triggered. We
      // make sure to update the video play state to "ended" if the video is
      // ended.
      if (event.target.ended) return;

      if (playPauseFeedback === true) {
        setVideoPlayState(VIDEO_PLAY_STATE_PAUSED);
        userInteractedTimeoutId.current = setTimeout(() => setHasUserInteracted(false), 600);
      }
    },
    ended: function() {
      if (playPauseFeedback === true) {
        setVideoPlayState(VIDEO_PLAY_STATE_ENDED);
        userInteractedTimeoutId.current = setTimeout(() => setHasUserInteracted(false), 600);
      }
    },
    error: function (event) {
      console.error(event.target.error);
      // setMediaError(event.target.error);
    },
  }), [playPauseFeedback]);

  const listeners = useMemo(() => ({
    ...defaultListeners,
    ...userListeners,
  }), [defaultListeners, userListeners]);

  const keyboardListeners = useMemo(() => ({
    Space: function() {
      if (element.ended) return;
      togglePlayVideo(element);
    },
  }), [element]);

  const [updatePreferences] = useMutation(updatePreferencesMutation, {
    onError: console.error,
  });

  const playStateTransition = useTransition(
    videoPlayState,
    null,
    playStateTransitionValuesFactory(videoPlayState, deviceInfo)
  );

  useEffect(function() {
    // Everytime the `source` changes we reset the mediaError state (which
    // mostly depends on the source media) to make sure we don't get stuck if
    // an unsupported (or invalid) source if temporally provided.
    setMediaError(DEFAULT_MEDIA_ERROR);
  }, [source]);

  useEffect(function() {
    if (attachMedia === null) return;
    if (element === null) return;

    attachMedia(element);
  }, [attachMedia, element]);

  useEffect(function() {
    if (element === null) return;

    Object.keys(listeners).forEach(type => (
      element.addEventListener(type, listeners[type])
    ));

    return function() {
      Object.keys(listeners).forEach(type => (
        element.removeEventListener(type, listeners[type])
      ));
    }
  }, [element, listeners]);

  useEffect(function() {
    if (element === null) return;
    if (keyboardShortcuts === false) return;

    const listeners = Object.keys(keyboardListeners).map(keyCode => (
      // We return a function that accepts event which will be the "keyup" event
      event => (
        // and make sure to only call the listener if the event.code is equal
        // to the key used to define the callback. For example, Space: fn should
        // be invoked only if the event.code is "Space".
        event.code === keyCode ? keyboardListeners[keyCode](event) : null
      )
    ));

    listeners.forEach(listener => (
      // @todo 2021-05-13
      //   Should be element.addEventListener instead to prevent binding the
      //   keyboard events on the entire page, which breaks every other keyboard
      //   functionality (eg. navigation, focus, etc.)
      window.document.addEventListener('keyup', listener)
    ));

    return function() {
      listeners.forEach(listener => (
        window.document.removeEventListener('keyup', listener)
      ));
    }
  }, [element, keyboardShortcuts, keyboardListeners]);

  useEffect(function() {
    // The consumer knows what it's doing and we let it override the user
    // preferences.
    if (muted !== undefined) return;

    if (element === null) return;
    if ( ! isQueryReady(me) || me.data === undefined) return;

    // Otherwise we sync the muted property to the user's preferences.
    element.muted = me.data.me.preferences.audioMuted;
  }, [element, me, muted]);

  useEffect(function() {
    if ( ! isQueryReady(me) || me.data === undefined) return;
    if (element === null) return;

    function onChange() {
      // When the video is not muted and is not paused (is playing with audio)
      // we need to mute the soundtrack.
      if ( ! element.muted && ! element.paused) {
        // noinspection JSIgnoredPromiseFromCall
        updatePreferences({ variables: { soundtrackMuted: true }});
      } else {
        // noinspection JSIgnoredPromiseFromCall
        updatePreferences({ variables: { soundtrackMuted: me.data.me.preferences.audioMuted }});
      }
    }

    element.addEventListener('play', onChange, false);
    element.addEventListener('pause', onChange, false);
    element.addEventListener('volumechange', onChange, false);
    onChange();

    return function() {
      element.removeEventListener('play', onChange, false);
      element.removeEventListener('pause', onChange, false);
      element.removeEventListener('volumechange', onChange, false);

      // When we unmount the video component, we will synchronize the soundtrack
      // preference to the audio preference.
      // noinspection JSIgnoredPromiseFromCall
      updatePreferences({ variables: { soundtrackMuted: me.data.me.preferences.audioMuted }});
    }
  },  [element, me, updatePreferences]);

  switch (mediaError.code) {
    case MediaError.MEDIA_ERR_ABORTED:
    case MediaError.MEDIA_ERR_NETWORK:
    case MediaError.MEDIA_ERR_DECODE:
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return (
        <MediaErrorContainer
          className={props.className}
          style={props.style}
        >
          <MediaErrorInner>
            <Heading level={3} align="center">{t('title.unsupportedVideoSource')}</Heading>
            <Text align="center" style={{ color: '#aaa' }}>
              {t('text.unsupportedVideoSourceDescription')}
            </Text>
          </MediaErrorInner>
        </MediaErrorContainer>
      );

    default:
      return (
        <VideoContainer
          style={style}
          hasPlayPauseFeedback={playPauseFeedback}
        >
          <video
            key={loadedSource}
            ref={node => {
              if (typeof forwardingReference === 'function') {
                forwardingReference(node)
              }

              reference(node);
            }}
            style={videoStyle}
            playsInline
            crossOrigin="anonymous"
            disablePictureInPicture
            controlsList="nodownload noremoteplayback"
            muted={muted}
            {...props}
            onClick={() => { props.onClick && props.onClick() ; setHasUserInteracted(true)}}
          />

          {playStateTransition.map(({ item, key, props }) => (
            <animated.div key={key} style={props}>
              {deviceInfo.isMobile && item === VIDEO_PLAY_STATE_PAUSED && !hasUserInteracted && (
                <VideoPlayPauseIcon icon={IconPlay} />
              )}
              {item === VIDEO_PLAY_STATE_PAUSED && hasUserInteracted ? (
                deviceInfo.isMobile ? (
                  <VideoPlayPauseIcon icon={IconPlay} />
                ) : (
                  <VideoPlayPauseIcon icon={IconPause} />
                )
              ) : item === VIDEO_PLAY_STATE_PLAYING && hasUserInteracted ? (
                <VideoPlayPauseIcon icon={IconPlay} />
              ) : null}
            </animated.div>
          ))}
        </VideoContainer>
      );
  }
}));

const VideoContainer = styled.div`

  ${props => props.hasPlayPauseFeedback && css`
    // We need to set the position to relative to correctly position the
    // play/pause icon shown on state changes.
    position: relative;
  `};
`;

const MediaErrorContainer = styled(props => (
  <Box padding="gutter" {...props} />
))`
  color: #aaa;
  background-color: #333;
`;

const MediaErrorInner = styled(props => (
  <Stack align="center" space="xsmall" {...props} />
))`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const videoPlayPauseIconStyle = {
  width: '4rem',
  height: '4rem',
};

const VideoPlayPauseIcon = styled(({
  icon: Icon,
  ...props
}) => (
  <Box padding="xlarge" {...props}>
    <Icon style={videoPlayPauseIconStyle} />
  </Box>
))`
  color: #fff;
  border-radius: 100%;
  font-size: 0;
`;

